import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

// Featured products data
const featuredProducts = [
  {
    id: 1,
    name: 'Premium Saffron',
    category: 'exotic-spices',
    image: '/Exotic Spice/saffron.jpg',
    description: 'The world\'s most precious spice, harvested from the stigmas of crocus flowers.'
  },
  {
    id: 2,
    name: 'Organic Turmeric',
    category: 'organic-spices',
    image: '/Ground Spice/Turmeric-Ground.jpg',
    description: 'Vibrant yellow turmeric powder, grown organically for maximum health benefits.'
  },
  {
    id: 3,
    name: 'Garam Masala',
    category: 'spice-blends',
    image: '/Spice Blend/garammasala.jpg',
    description: 'A traditional Indian spice blend featuring cinnamon, cardamom, cloves, and more.'
  },
  {
    id: 4,
    name: 'Whole Black Peppercorns',
    category: 'whole-spices',
    image: '/Whole Spice/black_peppercorns.jpg',
    description: 'Premium black peppercorns with a complex flavor profile and intense aroma.'
  }
];

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-spice-brown to-spice-red text-white">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="container-custom relative z-10 py-24 md:py-32">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Discover the World's Finest Spices
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              From exotic saffron to everyday essentials, we bring authentic flavors from around the globe to your kitchen.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products" className="btn btn-primary px-8 py-3 text-lg">
                Explore Our Products
              </Link>
              <Link to="/about" className="btn bg-white text-spice-red hover:bg-primary-100 px-8 py-3 text-lg">
                Our Story
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-primary-50">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Featured Products</h2>
            <p className="text-lg text-primary-600 max-w-2xl mx-auto">
              Discover our most popular spices and blends, carefully sourced and processed to preserve their authentic flavors.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {featuredProducts.map((product) => (
              <motion.div 
                key={product.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                variants={itemVariants}
              >
                <div className="h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                  <p className="text-primary-600 mb-4">{product.description}</p>
                  <Link 
                    to={`/products/${product.category}`} 
                    className="text-spice-red font-medium hover:text-spice-brown transition-colors duration-300"
                  >
                    Learn more →
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn btn-primary px-6 py-3">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Commitment to Quality</h2>
              <p className="text-lg text-primary-600 mb-6">
                At D.D. EXIM, we've been sourcing the finest spices from around the world since 1910. Our commitment to quality, sustainability, and authentic flavors has made us a trusted name in the industry.
              </p>
              <p className="text-lg text-primary-600 mb-8">
                Every spice in our collection is carefully selected, processed, and packaged to preserve its natural aroma and flavor. We work directly with farmers to ensure ethical sourcing and the highest quality products.
              </p>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </motion.div>
            
            <motion.div
              className="rounded-lg overflow-hidden shadow-xl"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="/Category/whole.avif" 
                alt="Spice farm" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certification Banner */}
      <section className="py-12 bg-spice-yellow bg-opacity-20">
        <div className="container-custom">
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-between gap-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h3 className="text-2xl font-bold mb-2">Certified Organic & Quality Assured</h3>
              <p className="text-primary-600">
                Our products meet the highest standards of quality and safety.
              </p>
            </div>
            <Link to="/certification" className="btn btn-primary whitespace-nowrap">
              View Our Certifications
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 